import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Wine, Search, Printer, Check, X, Loader2, 
  Building2, ChevronDown, ChevronUp, Edit2, Save,
  Globe, MapPin, Sparkles, Euro, Percent, Filter,
  FileText, Settings, RefreshCw, FolderOpen, Bookmark, Trash2,
  ChevronLeft, Upload, FileUp, Wand2, Plus, Download, Sheet, FileType
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiHeaders } from '../utils/apiHelpers';
import { CartaViniQRModal } from '../components/vini/CartaViniQRModal';
import { VerificaCartaAIDialog } from '../components/vini/VerificaCartaAIDialog';
import { VecchiaCartaDialog } from '../components/vini/VecchiaCartaDialog';
import { DescrizioniSommelierDialog } from '../components/vini/DescrizioniSommelierDialog';
import { DoppioniDialog } from '../components/vini/DoppioniDialog';
import { AggiornaPrezziFTDialog } from '../components/vini/AggiornaPrezziFTDialog';
import { VerificaCostiDialog } from '../components/vini/VerificaCostiDialog';
import { useDescrizioniDaAggiornare } from '../components/vini/useDescrizioniDaAggiornare';
import { AbbinaImmagineModal } from '../components/vini/AbbinaImmagineModal';
import { buildCartaViniHtml } from '../components/vini/stampaCartaVini';
import { QrCode, Image as ImageIcon, ArchiveRestore, PenLine, GitMerge, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache key per sessionStorage
const VINI_CACHE_KEY = 'ristorantissimo_vini_cache_v2';
const VINI_CACHE_TIMESTAMP_KEY = 'ristorantissimo_vini_cache_v2_ts';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minuti

// Funzioni cache
const getViniFromCache = () => {
  try {
    const cached = sessionStorage.getItem(VINI_CACHE_KEY);
    const timestamp = sessionStorage.getItem(VINI_CACHE_TIMESTAMP_KEY);
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_DURATION_MS) {
        return JSON.parse(cached);
      }
    }
  } catch (e) {
    console.warn('Errore lettura cache vini:', e);
  }
  return null;
};

const setViniCache = (data) => {
  try {
    sessionStorage.setItem(VINI_CACHE_KEY, JSON.stringify(data));
    sessionStorage.setItem(VINI_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (e) {
    console.warn('Errore salvataggio cache vini:', e);
  }
};

const clearViniCache = () => {
  try {
    sessionStorage.removeItem(VINI_CACHE_KEY);
    sessionStorage.removeItem(VINI_CACHE_TIMESTAMP_KEY);
  } catch (e) {
    console.warn('Errore pulizia cache vini:', e);
  }
};

// Categorie vino per raggruppamento
const TIPO_VINO_ORDER = ['champagne', 'bollicine', 'spumante', 'prosecco', 'bianco', 'rosato', 'rosso', 'dolce', 'altro'];
const TIPO_VINO_LABELS = {
  'champagne': 'Champagne',
  'bollicine': 'Bollicine',
  'spumante': 'Spumanti',
  'prosecco': 'Prosecco',
  'bianco': 'Vini Bianchi',
  'rosato': 'Vini Rosati',
  'rosso': 'Vini Rossi',
  'dolce': 'Vini Dolci e Passiti',
  'altro': 'Altri Vini'
};

// Detect if mobile
const isMobile = () => window.innerWidth < 768;

export default function ListaViniPage({ onBack }) {
  const [vini, setVini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedVini, setSelectedVini] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showQRCarta, setShowQRCarta] = useState(false);
  const [showVerificaAI, setShowVerificaAI] = useState(false);
  const [showVecchiaCarta, setShowVecchiaCarta] = useState(false);
  const [showDescrizioni, setShowDescrizioni] = useState(false);
  const [showDoppioni, setShowDoppioni] = useState(false);
  const [aggiornandoFT, setAggiornandoFT] = useState(false);
  const [riepilogoFT, setRiepilogoFT] = useState(null);
  const [showVerificaCosti, setShowVerificaCosti] = useState(false);
  const [showRicarico, setShowRicarico] = useState(false);
  const [ricarico, setRicarico] = useState({ moltiplicatore: '', soglia_minima: '' });
  const [vinoPerImmagine, setVinoPerImmagine] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const printRef = useRef();
  
  // Filtri
  const [filtri, setFiltri] = useState({ nazioni: [], regioni: [], tipi: [], fornitori: [] });
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroNazione, setFiltroNazione] = useState('');
  const [filtroRegione, setFiltroRegione] = useState('');
  const [filtroFornitore, setFiltroFornitore] = useState('');
  
  // Marginalità e prezzi
  const [marginalitaDefault, setMarginalitaDefault] = useState(70); // 70% default
  const [editingVino, setEditingVino] = useState(null);
  const [tempPrezzoVendita, setTempPrezzoVendita] = useState('');
  const [editingNomeVino, setEditingNomeVino] = useState(null);
  const [tempNomeVino, setTempNomeVino] = useState('');
  
  // Generazione descrizioni AI
  const [generandoDescrizioni, setGenerandoDescrizioni] = useState(false);
  const [progressGen, setProgressGen] = useState({ current: 0, total: 0, vino: '' });
  
  // Stats
  const [stats, setStats] = useState(null);

  // Impostazioni stampa
  const [stampaSettings, setStampaSettings] = useState({
    nomeRistorante: '',
    mostraDescrizione: false, // Default: solo nomi vini
    mostraPrezzi: true,
    mostraCopertina: false,
    mostraLiquori: false, // Default: NON stampare liquori
    soloLiquori: false,
    mostraFormato: false, // Default: NON mostrare formato bottiglia (0,75L etc)
    numeroPagine: '0', // 0 = auto
    margineSup: 15, // mm
    margineInf: 15, // mm
    margineLat: 10  // mm
  });

  // Liquori
  const [liquori, setLiquori] = useState([]);
  const [loadingLiquori, setLoadingLiquori] = useState(false);

  // Selezioni salvate
  const [selezioni, setSelezioni] = useState([]);
  const [loadingSelezioni, setLoadingSelezioni] = useState(false);
  const [showSalvaSelezione, setShowSalvaSelezione] = useState(false);
  const [nomeSelezione, setNomeSelezione] = useState('');
  const [descrizioneSelezione, setDescrizioneSelezione] = useState('');
  const [showSelezioniDropdown, setShowSelezioniDropdown] = useState(false);

  // Upload fattura
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFattura, setUploadingFattura] = useState(false);
  const [viniDaFattura, setViniDaFattura] = useState([]);
  const [selectedViniUpload, setSelectedViniUpload] = useState(new Set());
  const fileInputRef = useRef(null);

  // Categorizzazione e aggiunta manuale
  const [categorizzando, setCategorizzando] = useState(false);
  const [showAggiungiVino, setShowAggiungiVino] = useState(false);
  const [nuovoVino, setNuovoVino] = useState({
    nome: '',
    tipo_vino: '',
    produttore: '',
    regione: '',
    nazione: 'Italia',
    annata: '',
    formato: '750ml',
    prezzo_acquisto: '',
    prezzo_vendita: '',
  });

  // Riclassificazione AI
  const [riclassificando, setRiclassificando] = useState(false);
  const [generandoSingoloId, setGenerandoSingoloId] = useState(null);

  // Usa l'helper centralizzato per gli headers
  const getAuthHeaders = getApiHeaders;

  // Carica vini dall'endpoint avanzato (dalle fatture)
  // Usa cache per evitare ricaricamenti ad ogni apertura
  const loadVini = useCallback(async (forceRefresh = false) => {
    // Prova prima dalla cache se non è un refresh forzato
    if (!forceRefresh) {
      const cachedData = getViniFromCache();
      // Usa cache SOLO se ha vini validi
      if (cachedData && cachedData.vini && cachedData.vini.length > 0) {
        console.log('📦 Vini caricati da cache:', cachedData.vini.length);
        setVini(cachedData.vini);
        setStats(cachedData.stats || null);
        setFiltri(cachedData.filtri || { nazioni: [], regioni: [], tipi: [] });
        setLoading(false); // Importante: setta loading a false quando usa la cache
        return;
      }
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Carica TUTTI i vini (senza paginazione)
      params.append('per_page', '500');

      const res = await axios.get(`${API}/api/magazzino/vini-avanzato?${params}`, {
        headers: getAuthHeaders()
      });
      
      // I vini hanno già il prezzo di vendita calcolato/salvato
      const viniConPrezzi = (res.data.vini || []).map(v => ({
        ...v,
        margine_perc: v.margine_perc || 0
      }));
      
      setVini(viniConPrezzi);
      setStats(res.data.statistiche || null);
      setFiltri(res.data.filtri_disponibili || { nazioni: [], regioni: [], tipi: [] });
      
      // Salva in cache SOLO se ci sono vini
      if (viniConPrezzi.length > 0) {
        setViniCache({
          vini: viniConPrezzi,
          stats: res.data.statistiche,
          filtri: res.data.filtri_disponibili
        });
      }
      
      console.log('🍷 Vini caricati dal server:', viniConPrezzi.length);
    } catch (error) {
      console.error('Errore caricamento vini:', error);
      toast.error('Errore nel caricamento dei vini');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica vini all'avvio (usa cache se disponibile)
  useEffect(() => {
    loadVini(false); // Non forzare refresh, usa cache
  }, []);

  const ricaricaVini = useCallback(() => { clearViniCache(); loadVini(true); }, [loadVini]);
  useDescrizioniDaAggiornare(vini, getAuthHeaders, ricaricaVini);

  // Funzione per forzare refresh (invalida cache)
  const refreshVini = useCallback(() => {
    clearViniCache();
    loadVini(true);
    toast.success('Lista vini aggiornata');
  }, [loadVini]);

  // "Aggiorna da Fatture": rilegge le fatture e applica gli aumenti di costo alla carta (solo su richiesta)
  const aggiornaDaFatture = async () => {
    setAggiornandoFT(true);
    try {
      const res = await axios.post(`${API}/api/magazzino/vini/aggiorna-prezzi-ft`, {}, { headers: getAuthHeaders() });
      clearViniCache();
      await loadVini(true);
      loadLiquori();
      setRiepilogoFT(res.data);
      const n = res.data.aumenti?.length || 0;
      const r = res.data.riduzioni?.length || 0;
      const c = res.data.corretti?.length || 0;
      toast.success(n ? `${n} vini con aumenti: prezzi in carta aggiornati` : (r ? `Nessun aumento · ${r} costi ridotti (carta invariata)` : (c ? `${c} costi corretti` : 'Prezzi verificati: nessuna variazione')));
    } catch (error) {
      console.error('Errore aggiornamento prezzi da fatture:', error);
      toast.error('Errore durante la verifica dei prezzi');
    } finally {
      setAggiornandoFT(false);
    }
  };

  // Filtra vini lato client (già tutti caricati)
  const viniFiltrati = React.useMemo(() => {
    console.log('🔍 RICERCA VINI - searchQuery:', searchQuery, '- Totale vini:', vini.length);
    const risultati = vini.filter(v => {
      // Filtro ricerca - SOLO nel nome e cantina - VERSIONE 3
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nomeMatch = (v.nome || '').toLowerCase().includes(q);
        const cantinaMatch = (v.cantina || '').toLowerCase().includes(q);
        const matchSearch = nomeMatch || cantinaMatch;
        // Debug: mostra solo i primi match
        if (matchSearch && searchQuery.length > 2) {
          console.log(`✓ Match: "${v.nome}" - nome:${nomeMatch}, cantina:${cantinaMatch}`);
        }
        if (!matchSearch) return false;
      }
      // Filtro tipo
      if (filtroTipo && v.tipo_vino !== filtroTipo) return false;
      // Filtro nazione
      if (filtroNazione && v.nazione !== filtroNazione) return false;
      // Filtro regione
      if (filtroRegione && v.regione !== filtroRegione) return false;
      // Filtro fornitore
      if (filtroFornitore && v.fornitore !== filtroFornitore) return false;
      
      return true;
    });
    console.log('🔍 Risultati filtrati:', risultati.length);
    return risultati;
  }, [vini, searchQuery, filtroTipo, filtroNazione, filtroRegione, filtroFornitore]);

  // Carica liquori
  const loadLiquori = useCallback(async () => {
    setLoadingLiquori(true);
    try {
      const res = await axios.get(`${API}/api/magazzino/liquori-avanzato`, {
        headers: getAuthHeaders()
      });
      setLiquori(res.data.liquori || []);
    } catch (error) {
      console.error('Errore caricamento liquori:', error);
    } finally {
      setLoadingLiquori(false);
    }
  }, []);

  useEffect(() => {
    loadLiquori();
  }, [loadLiquori]);

  // Carica selezioni salvate
  const loadSelezioni = useCallback(async () => {
    setLoadingSelezioni(true);
    try {
      const res = await axios.get(`${API}/api/magazzino/selezioni`, {
        headers: getAuthHeaders()
      });
      setSelezioni(res.data.selezioni || []);
    } catch (error) {
      console.error('Errore caricamento selezioni:', error);
    } finally {
      setLoadingSelezioni(false);
    }
  }, []);

  useEffect(() => {
    loadSelezioni();
  }, [loadSelezioni]);

  // Salva selezione corrente
  const handleSalvaSelezione = async () => {
    if (!nomeSelezione.trim()) {
      toast.error('Inserisci un nome per la selezione');
      return;
    }
    if (selectedVini.size === 0) {
      toast.error('Seleziona almeno un vino');
      return;
    }
    
    try {
      await axios.post(`${API}/api/magazzino/selezioni`, {
        nome: nomeSelezione.trim(),
        vini_ids: Array.from(selectedVini),
        descrizione: descrizioneSelezione.trim() || null
      }, { headers: getAuthHeaders() });
      
      toast.success(`Selezione "${nomeSelezione}" salvata con ${selectedVini.size} vini`);
      setShowSalvaSelezione(false);
      setNomeSelezione('');
      setDescrizioneSelezione('');
      loadSelezioni();
    } catch (error) {
      console.error('Errore salvataggio selezione:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  // Ricarico vini: moltiplicatore generico + soglia minima
  const apriRicarico = async () => {
    try {
      const r = await axios.get(`${API}/api/magazzino/vini-pricing`, { headers: getAuthHeaders() });
      setRicarico({ moltiplicatore: r.data.moltiplicatore || '', soglia_minima: r.data.soglia_minima || '' });
    } catch (e) { /* usa valori correnti */ }
    setShowRicarico(true);
  };

  const salvaRicarico = async () => {
    try {
      await axios.post(`${API}/api/magazzino/vini-pricing`, {
        moltiplicatore: parseFloat(ricarico.moltiplicatore) || 0,
        soglia_minima: parseFloat(ricarico.soglia_minima) || 0
      }, { headers: getAuthHeaders() });
      toast.success('Ricarico salvato: prezzi automatici aggiornati');
      setShowRicarico(false);
      clearViniCache();
      loadVini(true);
    } catch (error) {
      toast.error('Errore nel salvataggio del ricarico');
    }
  };

  // Pubblica la selezione corrente sulla Carta Vini QR pubblica
  const handlePubblicaSelezione = async () => {
    if (selectedVini.size === 0) {
      toast.error('Seleziona prima i vini da pubblicare (checkbox)');
      return;
    }
    if (!window.confirm(`Pubblicare ${selectedVini.size} vini sulla Carta QR pubblica?\nSolo questi vini saranno visibili ai clienti che scansionano il QR.`)) return;
    try {
      await axios.post(`${API}/api/carta-vini/selezione`, {
        vini_ids: Array.from(selectedVini)
      }, { headers: getAuthHeaders() });
      toast.success(`Carta pubblica aggiornata: ${selectedVini.size} vini pubblicati`);
    } catch (error) {
      console.error('Errore pubblicazione selezione:', error);
      toast.error('Errore nella pubblicazione');
    }
  };

  // Carica una selezione salvata
  const handleCaricaSelezione = (selezione) => {
    const viniIds = new Set(selezione.vini_ids || []);
    // Filtra solo gli ID che esistono nei vini caricati
    const viniValidi = vini.filter(v => viniIds.has(v.id));
    const nuoviSelezionati = new Set(viniValidi.map(v => v.id));
    
    setSelectedVini(nuoviSelezionati);
    setSelectAll(nuoviSelezionati.size === vini.length);
    setShowSelezioniDropdown(false);
    
    const nonTrovati = selezione.vini_ids.length - nuoviSelezionati.size;
    if (nonTrovati > 0) {
      toast.info(`Caricata selezione "${selezione.nome}" - ${nuoviSelezionati.size} vini (${nonTrovati} non più disponibili)`);
    } else {
      toast.success(`Caricata selezione "${selezione.nome}" - ${nuoviSelezionati.size} vini`);
    }
  };

  // Elimina una selezione
  const handleEliminaSelezione = async (selezione, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare la selezione "${selezione.nome}"?`)) return;
    
    try {
      await axios.delete(`${API}/api/magazzino/selezioni/${selezione.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Selezione eliminata');
      loadSelezioni();
    } catch (error) {
      console.error('Errore eliminazione selezione:', error);
      toast.error('Errore eliminazione');
    }
  };

  // ============================================
  // UPLOAD FATTURA
  // ============================================
  const handleUploadFattura = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seleziona un file PDF');
      return;
    }
    
    setUploadingFattura(true);
    setViniDaFattura([]);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/api/magazzino/vini/upload-fattura`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        setViniDaFattura(res.data.vini || []);
        setSelectedViniUpload(new Set(res.data.vini.map(v => v.id)));
        toast.success(`Trovati ${res.data.vini.length} vini nella fattura`);
      }
    } catch (error) {
      console.error('Errore upload fattura:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'elaborazione del PDF');
    } finally {
      setUploadingFattura(false);
      // Reset input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportaViniUpload = async () => {
    const viniSelezionati = viniDaFattura.filter(v => selectedViniUpload.has(v.id));
    
    if (viniSelezionati.length === 0) {
      toast.error('Seleziona almeno un vino da importare');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/magazzino/vini/importa-da-upload`, viniSelezionati, {
        headers: getAuthHeaders()
      });
      
      if (res.data.success) {
        toast.success(`Importati ${res.data.importati} vini, aggiornati ${res.data.aggiornati}`);
        setShowUploadModal(false);
        setViniDaFattura([]);
        setSelectedViniUpload(new Set());
        // IMPORTANTE: Pulisci la cache per forzare il ricaricamento dal server
        clearViniCache();
        loadVini(true); // Forza ricaricamento dal server
      }
    } catch (error) {
      console.error('Errore importazione vini:', error);
      toast.error('Errore durante l\'importazione');
    }
  };

  const toggleVinoUpload = (vinoId) => {
    const newSelected = new Set(selectedViniUpload);
    if (newSelected.has(vinoId)) {
      newSelected.delete(vinoId);
    } else {
      newSelected.add(vinoId);
    }
    setSelectedViniUpload(newSelected);
  };

  // ============================================
  // CATEGORIZZA TUTTI I VINI
  // ============================================
  const handleCategorizzaTutti = async () => {
    if (categorizzando) return;
    
    setCategorizzando(true);
    toast.info('Categorizzazione AI avviata...');
    
    try {
      const res = await axios.post(`${API}/api/magazzino/vini/categorizza-tutti`, {}, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        if (res.data.in_background) {
          // La categorizzazione è in background
          toast.success(`Categorizzazione AI avviata per ${res.data.totale} vini! Ricarica la pagina tra 10-30 secondi.`);
          // Auto-reload dopo 15 secondi
          setTimeout(() => {
            loadVini();
            toast.info('Lista vini aggiornata!');
          }, 15000);
        } else {
          toast.success(`Ri-categorizzati ${res.data.aggiornati} vini su ${res.data.totale}`);
          loadVini();
        }
      } else {
        toast.error('Errore durante la categorizzazione');
      }
    } catch (error) {
      console.error('Errore categorizzazione:', error);
      toast.error('Errore durante la categorizzazione');
    } finally {
      setCategorizzando(false);
    }
  };

  // ============================================
  // AGGIUNGI VINO MANUALE
  // ============================================
  const handleAggiungiVinoManuale = async () => {
    if (!nuovoVino.nome.trim()) {
      toast.error('Il nome del vino è obbligatorio');
      return;
    }
    
    try {
      const vinoData = {
        ...nuovoVino,
        prezzo_acquisto: parseFloat(nuovoVino.prezzo_acquisto) || 0,
        prezzo_vendita: parseFloat(nuovoVino.prezzo_vendita) || 0,
      };
      
      const res = await axios.post(`${API}/api/magazzino/vini/aggiungi-manuale`, vinoData, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        toast.success(`Vino "${nuovoVino.nome}" aggiunto con successo`);
        setShowAggiungiVino(false);
        setNuovoVino({
          nome: '',
          tipo_vino: '',
          produttore: '',
          regione: '',
          nazione: 'Italia',
          annata: '',
          formato: '750ml',
          prezzo_acquisto: '',
          prezzo_vendita: '',
        });
        loadVini(); // Ricarica la lista
      } else {
        toast.error(res.data.message || 'Errore durante l\'aggiunta');
      }
    } catch (error) {
      console.error('Errore aggiunta vino:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiunta del vino');
    }
  };

  // ============================================
  // GENERA DESCRIZIONE AI SINGOLO VINO
  // ============================================
  const handleGeneraDescrizioneSingolo = async (vino, e) => {
    e?.stopPropagation();
    
    if (generandoSingoloId) {
      toast.error('Attendere il completamento della generazione corrente');
      return;
    }
    
    setGenerandoSingoloId(vino.id);
    
    try {
      // Usa l'endpoint esistente per generare descrizione
      const res = await axios.post(`${API}/api/gestione/ricette/vino-genera-descrizione`, {
        nome_vino: vino.nome,
        nome_originale: vino.nome_originale || vino.nome,  // Nome completo dalla fattura
        regione: vino.regione || '',
        tipo_vino: vino.tipo_vino || '',
        fornitore: vino.fornitore || '',
        cantina: vino.cantina || ''
      }, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        // Salva i dati generati (inclusi nuovi campi)
        await axios.post(`${API}/api/magazzino/vini/aggiorna`, {
          vino_id: vino.id,
          descrizione: res.data.descrizione,
          vitigno: res.data.uvaggio,
          gradazione: res.data.gradazione,
          abbinamenti: res.data.abbinamenti,
          riconoscimenti: res.data.riconoscimenti || '',
          cantina: res.data.cantina || vino.cantina,
          zona_produzione: res.data.zona_produzione,
          regione: res.data.regione || '',
          formato: res.data.formato
        }, { headers: getAuthHeaders() });
        
        // Aggiorna localmente
        setVini(prev => prev.map(v => 
          v.id === vino.id 
            ? { 
                ...v, 
                descrizione: res.data.descrizione,
                vitigno: res.data.uvaggio,
                gradazione: res.data.gradazione,
                abbinamenti: res.data.abbinamenti,
                riconoscimenti: res.data.riconoscimenti || '',
                cantina: res.data.cantina || v.cantina,
                zona_produzione: res.data.zona_produzione,
                regione: res.data.regione || v.regione,
                formato: res.data.formato
              }
            : v
        ));
        
        // Invalida cache per vedere le modifiche alla prossima visita
        clearViniCache();
        
        toast.success(`Descrizione generata per "${vino.nome}"`);
      }
    } catch (error) {
      console.error('Errore generazione descrizione:', error);
      toast.error('Errore durante la generazione AI');
    } finally {
      setGenerandoSingoloId(null);
    }
  };

  // Aggiorna liquore (prezzo menu e includi stampa)
  const updateLiquore = async (liquoreId, field, value) => {
    try {
      await axios.post(`${API}/api/magazzino/liquori/aggiorna`, {
        liquore_id: liquoreId,
        [field]: value
      }, { headers: getAuthHeaders() });
      
      // Aggiorna stato locale
      setLiquori(prev => prev.map(l => 
        l.id === liquoreId ? { ...l, [field]: value } : l
      ));
      
      toast.success('Salvato');
    } catch (error) {
      console.error('Errore aggiornamento liquore:', error);
      toast.error('Errore salvataggio');
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Chiudi dropdown selezioni quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSelezioniDropdown && !e.target.closest('[data-testid="carica-selezione-btn"]') && !e.target.closest('[data-dropdown-selezioni]')) {
        setShowSelezioniDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSelezioniDropdown]);

  // Dopo un'unione di doppioni o un ricaricamento, toglie dalla selezione gli id che non esistono più
  useEffect(() => {
    const ids = new Set(vini.map(v => v.id));
    setSelectedVini(prev => {
      const n = [...prev].filter(id => ids.has(id));
      return n.length === prev.size ? prev : new Set(n);
    });
  }, [vini]);

  // Seleziona tutti i vini automaticamente quando vengono caricati
  useEffect(() => {
    if (vini.length > 0 && selectedVini.size === 0) {
      setSelectedVini(new Set(vini.map(v => v.id)));
      setSelectAll(true);
    }
  }, [vini]);

  // Toggle selezione vino
  const toggleVino = (vinoId) => {
    const newSelected = new Set(selectedVini);
    if (newSelected.has(vinoId)) {
      newSelected.delete(vinoId);
    } else {
      newSelected.add(vinoId);
    }
    setSelectedVini(newSelected);
    setSelectAll(newSelected.size === viniFiltrati.length);
  };

  // Seleziona tutti (solo vini filtrati attualmente visibili)
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedVini(new Set());
    } else {
      setSelectedVini(new Set(viniFiltrati.map(v => v.id)));
    }
    setSelectAll(!selectAll);
  };

  // Aggiorna prezzo di vendita per un vino
  const handleSavePrezzoVendita = async (vino) => {
    const nuovoPrezzo = parseFloat(tempPrezzoVendita);
    if (isNaN(nuovoPrezzo) || nuovoPrezzo <= 0) {
      toast.error('Inserisci un prezzo valido');
      return;
    }
    
    try {
      // Aggiorna tramite nuovo endpoint magazzino
      await axios.post(`${API}/api/magazzino/vini/aggiorna`, {
        vino_id: vino.id,
        prezzo_vendita: nuovoPrezzo
      }, { headers: getAuthHeaders() });
      
      // Aggiorna localmente
      setVini(prev => prev.map(v => 
        v.id === vino.id 
          ? { 
              ...v, 
              prezzo_vendita: nuovoPrezzo,
              margine_perc: v.prezzo_acquisto > 0 
                ? Math.round((nuovoPrezzo - v.prezzo_acquisto) / nuovoPrezzo * 100) 
                : 0
            }
          : v
      ));
      
      // Invalida cache
      clearViniCache();
      
      toast.success('Prezzo aggiornato!');
      setEditingVino(null);
    } catch (error) {
      console.error('Errore aggiornamento prezzo:', error);
      toast.error('Errore aggiornamento prezzo');
    }
  };

  // Salva nome vino modificato manualmente
  const handleSaveNomeVino = async (vino) => {
    const nuovoNome = tempNomeVino.trim();
    if (!nuovoNome) {
      toast.error('Inserisci un nome valido');
      return;
    }
    
    try {
      await axios.post(`${API}/api/magazzino/vini/aggiorna`, {
        vino_id: vino.id,
        nome: nuovoNome
      }, { headers: getAuthHeaders() });
      
      // Aggiorna localmente
      setVini(prev => prev.map(v => 
        v.id === vino.id 
          ? { ...v, nome: nuovoNome, nome_modificato_manualmente: true }
          : v
      ));
      
      // Invalida cache
      clearViniCache();
      
      toast.success('Nome vino aggiornato!');
      setEditingNomeVino(null);
    } catch (error) {
      console.error('Errore aggiornamento nome:', error);
      toast.error('Errore aggiornamento nome');
    }
  };

  // Applica marginalità a tutti i vini selezionati
  const applicaMarginalitaATutti = async () => {
    if (selectedVini.size === 0) {
      toast.error('Seleziona almeno un vino');
      return;
    }
    
    const conferma = window.confirm(
      `Vuoi applicare la marginalità del ${marginalitaDefault}% a ${selectedVini.size} vini selezionati?`
    );
    if (!conferma) return;
    
    let aggiornati = 0;
    for (const vinoId of selectedVini) {
      const vino = vini.find(v => v.id === vinoId);
      if (vino && vino.prezzo_acquisto > 0) {
        const nuovoPrezzo = Math.ceil(vino.prezzo_acquisto / (1 - marginalitaDefault / 100));
        try {
          await axios.post(`${API}/api/magazzino/vini/aggiorna`, {
            vino_id: vino.id,
            prezzo_vendita: nuovoPrezzo
          }, { headers: getAuthHeaders() });
          aggiornati++;
        } catch (e) {
          console.error(`Errore per ${vino.nome}:`, e);
        }
      }
    }
    
    toast.success(`Aggiornati ${aggiornati} vini con marginalità ${marginalitaDefault}%`);
    loadVini();
  };

  // Genera descrizioni AI per vini senza descrizione
  // Genera descrizioni per una lista di vini (usato da Genera AI e Rigenera Lista)
  const generaDescrizioniPerLista = async (lista) => {
    setGenerandoDescrizioni(true);
    setProgressGen({ current: 0, total: lista.length, vino: '' });
    let successi = 0;
    for (let i = 0; i < lista.length; i++) {
      const vino = lista[i];
      setProgressGen({ current: i + 1, total: lista.length, vino: vino.nome });
      try {
        const res = await axios.post(`${API}/api/gestione/ricette/vino-genera-descrizione`, {
          nome_vino: vino.nome,
          nome_originale: vino.nome_originale || vino.nome,
          regione: vino.regione || '',
          tipo_vino: vino.tipo_vino || '',
          fornitore: vino.fornitore || '',
          cantina: vino.cantina || ''
        }, { headers: getAuthHeaders() });
        if (res.data.success) {
          await axios.post(`${API}/api/magazzino/vini/aggiorna`, {
            vino_id: vino.id,
            descrizione: res.data.descrizione,
            vitigno: res.data.uvaggio,
            gradazione: res.data.gradazione,
            abbinamenti: res.data.abbinamenti,
            riconoscimenti: res.data.riconoscimenti || '',
            cantina: res.data.cantina || vino.cantina,
            zona_produzione: res.data.zona_produzione,
            regione: res.data.regione || '',
            formato: res.data.formato
          }, { headers: getAuthHeaders() });
          successi++;
        }
      } catch (e) {
        console.error(`Errore per ${vino.nome}:`, e);
      }
      await new Promise(r => setTimeout(r, 300));
    }
    setGenerandoDescrizioni(false);
    setProgressGen({ current: 0, total: 0, vino: '' });
    return successi;
  };

  // RIGENERA TUTTA LA LISTA: riclassifica forzata + rigenera descrizioni di TUTTI i vini
  const handleRigeneraLista = async () => {
    const conferma = window.confirm(
      `⚠️ RIGENERARE TUTTA LA LISTA (${vini.length} vini)?\n\n1. Riclassificazione AI FORZATA: cantina, nazione, regione, tipo (sovrascrive i dati errati)\n2. Rigenerazione di TUTTE le descrizioni, uvaggi e riconoscimenti\n\nL'operazione richiede diversi minuti.`
    );
    if (!conferma) return;
    
    setRiclassificando(true);
    try {
      await eseguiRiclassifica(true);
      toast.success('✓ Riclassificazione completata, rigenero le descrizioni...');
    } catch (e) {
      toast.error('Errore durante la riclassificazione');
      setProgressGen({ current: 0, total: 0, vino: '' });
      setRiclassificando(false);
      return;
    }
    setProgressGen({ current: 0, total: 0, vino: '' });
    setRiclassificando(false);
    
    // Ricarica la lista fresca (con cantine/regioni corrette) prima di generare le descrizioni
    let listaFresca = vini;
    try {
      const r = await axios.get(`${API}/api/magazzino/vini-avanzato?per_page=500`, { headers: getAuthHeaders() });
      if (r.data.vini?.length > 0) listaFresca = r.data.vini;
    } catch (e) { /* usa lista in memoria */ }
    
    const successi = await generaDescrizioniPerLista(listaFresca);
    toast.success(`✓ Lista rigenerata: ${successi}/${listaFresca.length} vini completati`);
    clearViniCache();
    loadVini(true);
  };

  const handleGeneraDescrizioniAI = async () => {
    const viniSenzaDesc = vini.filter(v => selectedVini.has(v.id) && (!v.descrizione || v.descrizione.trim() === ''));
    
    if (viniSenzaDesc.length === 0) {
      toast.info('Tutti i vini selezionati hanno già una descrizione!');
      return;
    }
    
    const conferma = window.confirm(
      `Vuoi generare descrizioni AI per ${viniSenzaDesc.length} vini?`
    );
    if (!conferma) return;
    
    setGenerandoDescrizioni(true);
    setProgressGen({ current: 0, total: viniSenzaDesc.length, vino: '' });
    
    let successi = 0;
    for (let i = 0; i < viniSenzaDesc.length; i++) {
      const vino = viniSenzaDesc[i];
      setProgressGen({ current: i + 1, total: viniSenzaDesc.length, vino: vino.nome });
      
      try {
        const res = await axios.post(`${API}/api/gestione/ricette/vino-genera-descrizione`, {
          nome_vino: vino.nome,
          nome_originale: vino.nome_originale || vino.nome,  // Nome completo dalla fattura
          regione: vino.regione || '',
          tipo_vino: vino.tipo_vino || '',
          fornitore: vino.fornitore || '',
          cantina: vino.cantina || ''
        }, { headers: getAuthHeaders() });
        
        if (res.data.success) {
          // Salva tutti i dati generati dall'AI (inclusi nuovi campi)
          await axios.post(`${API}/api/magazzino/vini/aggiorna`, {
            vino_id: vino.id,
            descrizione: res.data.descrizione,
            vitigno: res.data.uvaggio,
            gradazione: res.data.gradazione,
            abbinamenti: res.data.abbinamenti,
            riconoscimenti: res.data.riconoscimenti || '',
            cantina: res.data.cantina || vino.cantina,
            zona_produzione: res.data.zona_produzione,
            regione: res.data.regione || '',
            formato: res.data.formato
          }, { headers: getAuthHeaders() });
          successi++;
        }
      } catch (e) {
        console.error(`Errore per ${vino.nome}:`, e);
      }
      
      // Pausa per non sovraccaricare l'API
      await new Promise(r => setTimeout(r, 500));
    }
    
    setGenerandoDescrizioni(false);
    setProgressGen({ current: 0, total: 0, vino: '' });
    toast.success(`Generate ${successi} descrizioni AI`);
    // IMPORTANTE: Forza refresh per invalidare la cache e vedere le nuove descrizioni
    clearViniCache();
    loadVini(true);
  };

  // Riclassifica vini con AI
  // Avvia riclassificazione in background e attende il completamento (polling)
  const eseguiRiclassifica = async (forza = false) => {
    await axios.post(`${API}/api/gestione/ricette/vini-riclassifica-ai`, forza ? { forza: true } : {}, {
      headers: getAuthHeaders(),
      timeout: 60000
    });
    // Polling stato (max 10 minuti)
    for (let i = 0; i < 200; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const st = await axios.get(`${API}/api/gestione/ricette/vini-riclassifica-stato`, { headers: getAuthHeaders() });
        const s = st.data || {};
        if (s.stato === 'done') return s;
        if (s.stato === 'error') throw new Error(s.errore || 'Errore riclassificazione');
        setProgressGen({ current: s.processati || 0, total: s.totale || 0, vino: 'Riclassificazione AI in corso...' });
      } catch (e) {
        if (e.message && e.message.includes('riclassificazione')) throw e;
      }
    }
    throw new Error('Timeout riclassificazione');
  };

  const handleRiclassificaAI = async () => {
    if (!window.confirm('Vuoi riclassificare TUTTI i vini con AI?\nVerranno corrette cantina, regione, nazione e tipo.')) return;
    setRiclassificando(true);
    try {
      const data = await eseguiRiclassifica(false);
      toast.success(`Riclassificati ${data.aggiornati} vini su ${data.processati}`);
      clearViniCache();
      loadVini(true);
    } catch (err) {
      toast.error('Errore riclassificazione: ' + (err.response?.data?.detail || err.message));
    }
    setProgressGen({ current: 0, total: 0, vino: '' });
    setRiclassificando(false);
  };

  // Elimina singolo vino
  const handleEliminaVino = async (vinoId, vinoNome) => {
    if (!window.confirm(`Eliminare "${vinoNome}" dalla lista vini?`)) return;
    try {
      await axios.delete(`${API}/api/magazzino/vini/${vinoId}`, { headers: getAuthHeaders() });
      toast.success(`"${vinoNome}" eliminato`);
      clearViniCache();
      loadVini(true);
    } catch (err) {
      toast.error('Errore eliminazione');
    }
  };

  // Pulisci non-vini (aceti, condimenti, etc.)
  const handlePulisciNonVini = async () => {
    if (!window.confirm('Rimuovere automaticamente prodotti non-vino (aceti, condimenti, etc.) dalla lista?')) return;
    try {
      const res = await axios.post(`${API}/api/magazzino/vini/pulisci-non-vini`, {}, {
        headers: getAuthHeaders()
      });
      const data = res.data;
      if (data.rimossi > 0) {
        toast.success(`Rimossi ${data.rimossi} prodotti non-vino: ${data.dettaglio.join(', ')}`);
        clearViniCache();
        loadVini(true);
      } else {
        toast.info('Nessun prodotto non-vino trovato');
      }
    } catch (err) {
      toast.error('Errore pulizia');
    }
  };



  // Raggruppa vini per tipo, poi nazione, poi regione
  const viniRaggruppati = useCallback(() => {
    const grouped = {};
    
    // USA viniFiltrati invece di vini per rispettare i filtri!
    viniFiltrati.forEach(vino => {
      const tipo = (vino.tipo_vino || 'altro').toLowerCase();
      const nazione = vino.nazione || 'Italia';
      const regione = vino.regione || 'Altro';
      
      if (!grouped[tipo]) grouped[tipo] = {};
      if (!grouped[tipo][nazione]) grouped[tipo][nazione] = {};
      if (!grouped[tipo][nazione][regione]) grouped[tipo][nazione][regione] = [];
      
      grouped[tipo][nazione][regione].push(vino);
    });
    
    return grouped;
  }, [viniFiltrati]);

  // Vini selezionati per stampa (dalla lista filtrata corrente)
  const viniDaStampare = viniFiltrati.filter(v => selectedVini.has(v.id));

  // Funzione stampa elegante
  const handlePrint = () => {
    if (viniDaStampare.length === 0) {
      toast.error('Seleziona almeno un vino da stampare');
      return;
    }
    setShowPrintPreview(true);
  };

  const executePrint = () => {
    if (viniDaStampare.length === 0 && !stampaSettings.soloLiquori) {
      toast.error('Nessun vino selezionato per la stampa');
      return;
    }
    const htmlDoc = buildCartaViniHtml(viniDaStampare, liquori, stampaSettings);
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      toast.error('Popup bloccato! Abilita i popup per stampare.');
      return;
    }
    win.document.open();
    win.document.write(htmlDoc);
    win.document.close();
  };

  // Raggruppamento per visualizzazione
  const grouped = viniRaggruppati();

  // Modali condivisi tra layout mobile e desktop
  const renderModali = () => (
    <>
      {/* Modal Ricarico Vini */}
      {showRicarico && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowRicarico(false)}>
          <div data-testid="ricarico-modal" className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 flex items-center gap-2"><Percent className="h-5 w-5 text-emerald-600" /> Ricarico Vini</h3>
              <button onClick={() => setShowRicarico(false)} data-testid="ricarico-chiudi"><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <label className="text-sm text-gray-600 font-medium">Moltiplicatore generico</label>
            <input type="number" step="0.1" min="0" placeholder="Es. 2.5 (vuoto = margine 70%)"
              value={ricarico.moltiplicatore}
              onChange={e => setRicarico(r => ({ ...r, moltiplicatore: e.target.value }))}
              data-testid="ricarico-moltiplicatore"
              className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" />
            <label className="text-sm text-gray-600 font-medium">Soglia minima di vendita (€)</label>
            <input type="number" step="1" min="0" placeholder="Es. 29"
              value={ricarico.soglia_minima}
              onChange={e => setRicarico(r => ({ ...r, soglia_minima: e.target.value }))}
              data-testid="ricarico-soglia"
              className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" />
            <p className="text-xs text-gray-500 mb-4">
              Prezzo automatico = acquisto × moltiplicatore. Se il risultato è sotto la soglia (es. 21€ con soglia 29€), viene alzato alla soglia. I prezzi impostati manualmente sui singoli vini NON vengono toccati.
            </p>
            <button onClick={salvaRicarico} data-testid="ricarico-salva"
              className="w-full py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center justify-center gap-2">
              <Save className="h-4 w-4" /> Salva e ricalcola prezzi
            </button>
          </div>
        </div>
      )}
      {/* Modal Aggiungi Vino Manuale */}
      {showAggiungiVino && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plus className="h-6 w-6" />
                <div>
                  <h2 className="text-lg font-bold">Aggiungi Vino</h2>
                  <p className="text-amber-100 text-sm">Inserisci i dettagli del vino</p>
                </div>
              </div>
              <button
                onClick={() => setShowAggiungiVino(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Form */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Vino *</label>
                <input
                  type="text"
                  value={nuovoVino.nome}
                  onChange={(e) => setNuovoVino({...nuovoVino, nome: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Es: Barolo DOCG Riserva"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={nuovoVino.tipo_vino}
                    onChange={(e) => setNuovoVino({...nuovoVino, tipo_vino: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Auto-rileva</option>
                    <option value="rosso">Rosso</option>
                    <option value="bianco">Bianco</option>
                    <option value="rosato">Rosato</option>
                    <option value="bollicine">Bollicine</option>
                    <option value="champagne">Champagne</option>
                    <option value="prosecco">Prosecco</option>
                    <option value="dolce">Dolce</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Formato</label>
                  <select
                    value={nuovoVino.formato}
                    onChange={(e) => setNuovoVino({...nuovoVino, formato: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="375ml">375ml (Mezza)</option>
                    <option value="750ml">750ml (Standard)</option>
                    <option value="1500ml">1500ml (Magnum)</option>
                    <option value="3000ml">3000ml (Jeroboam)</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Produttore/Cantina</label>
                  <input
                    type="text"
                    value={nuovoVino.produttore}
                    onChange={(e) => setNuovoVino({...nuovoVino, produttore: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Es: Antinori"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Annata</label>
                  <input
                    type="text"
                    value={nuovoVino.annata}
                    onChange={(e) => setNuovoVino({...nuovoVino, annata: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Es: 2020"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nazione</label>
                  <select
                    value={nuovoVino.nazione}
                    onChange={(e) => setNuovoVino({...nuovoVino, nazione: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="Italia">Italia</option>
                    <option value="Francia">Francia</option>
                    <option value="Spagna">Spagna</option>
                    <option value="Germania">Germania</option>
                    <option value="Portogallo">Portogallo</option>
                    <option value="Austria">Austria</option>
                    <option value="USA">USA</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Cile">Cile</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regione</label>
                  <input
                    type="text"
                    value={nuovoVino.regione}
                    onChange={(e) => setNuovoVino({...nuovoVino, regione: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Es: Piemonte"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Acquisto (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={nuovoVino.prezzo_acquisto}
                    onChange={(e) => setNuovoVino({...nuovoVino, prezzo_acquisto: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Vendita (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={nuovoVino.prezzo_vendita}
                    onChange={(e) => setNuovoVino({...nuovoVino, prezzo_vendita: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Auto-calcolato (70% margine)"
                  />
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowAggiungiVino(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleAggiungiVinoManuale}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Aggiungi Vino
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Upload Fattura */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileUp className="h-6 w-6" />
                <div>
                  <h2 className="text-lg font-bold">Importa Vini da Fattura</h2>
                  <p className="text-indigo-200 text-sm">Carica un PDF per estrarre i vini</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setViniDaFattura([]);
                  setSelectedViniUpload(new Set());
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Upload area */}
              {viniDaFattura.length === 0 && (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleUploadFattura}
                    className="hidden"
                    id="fattura-upload"
                  />
                  <label htmlFor="fattura-upload" className="cursor-pointer">
                    {uploadingFattura ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
                        <p className="text-gray-600">Elaborazione PDF in corso...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <Upload className="h-12 w-12 text-gray-400" />
                        <div>
                          <p className="text-lg font-medium text-gray-700">
                            Clicca per caricare una fattura PDF
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            Verranno estratti automaticamente i vini presenti
                          </p>
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              )}

              {/* Lista vini trovati */}
              {viniDaFattura.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      Trovati <span className="text-indigo-600 font-bold">{viniDaFattura.length}</span> vini
                    </p>
                    <button
                      onClick={() => {
                        if (selectedViniUpload.size === viniDaFattura.length) {
                          setSelectedViniUpload(new Set());
                        } else {
                          setSelectedViniUpload(new Set(viniDaFattura.map(v => v.id)));
                        }
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      {selectedViniUpload.size === viniDaFattura.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                    </button>
                  </div>

                  <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                    {viniDaFattura.map(vino => (
                      <div
                        key={vino.id}
                        onClick={() => toggleVinoUpload(vino.id)}
                        className={`p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                          selectedViniUpload.has(vino.id) ? 'bg-indigo-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedViniUpload.has(vino.id) 
                            ? 'bg-indigo-600 border-indigo-600' 
                            : 'border-gray-300'
                        }`}>
                          {selectedViniUpload.has(vino.id) && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{vino.nome}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {vino.tipo_vino && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                {vino.tipo_vino}
                              </span>
                            )}
                            {vino.regione && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {vino.regione}
                              </span>
                            )}
                          </div>
                        </div>
                        {vino.prezzo_acquisto && (
                          <span className="text-sm font-medium text-gray-700">
                            €{vino.prezzo_acquisto.toFixed(2)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {viniDaFattura.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => {
                    setViniDaFattura([]);
                    setSelectedViniUpload(new Set());
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Carica altra fattura
                </button>
                <button
                  onClick={handleImportaViniUpload}
                  disabled={selectedViniUpload.size === 0}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  Importa {selectedViniUpload.size} vini
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modale QR Carta Vini digitale */}
      <CartaViniQRModal open={showQRCarta} onClose={() => setShowQRCarta(false)} getAuthHeaders={getAuthHeaders} />
      <VerificaCartaAIDialog open={showVerificaAI} onClose={() => setShowVerificaAI(false)} vini={vini} getAuthHeaders={getAuthHeaders} onApplicate={() => loadVini(true)} />
      <DescrizioniSommelierDialog open={showDescrizioni} onClose={() => setShowDescrizioni(false)} getAuthHeaders={getAuthHeaders} onCompletato={() => { clearViniCache(); loadVini(true); }} />
      <DoppioniDialog open={showDoppioni} onClose={() => setShowDoppioni(false)} getAuthHeaders={getAuthHeaders} onCambiato={() => { clearViniCache(); loadVini(true); }} />
      <AggiornaPrezziFTDialog riepilogo={riepilogoFT} onClose={() => setRiepilogoFT(null)} />
      <VerificaCostiDialog open={showVerificaCosti} onClose={() => setShowVerificaCosti(false)} getAuthHeaders={getAuthHeaders} onCorretto={() => { clearViniCache(); loadVini(true); }} />
      <VecchiaCartaDialog open={showVecchiaCarta} onClose={() => setShowVecchiaCarta(false)} getAuthHeaders={getAuthHeaders} onAggiunti={async (ids) => { await loadVini(true); setSelectedVini(prev => new Set([...prev, ...ids])); }} />

      {/* Modale abbinamento immagine bottiglia */}
      <AbbinaImmagineModal
        vino={vinoPerImmagine}
        onClose={() => setVinoPerImmagine(null)}
        getAuthHeaders={getAuthHeaders}
        onSaved={(vinoId, url) => {
          setVini(prev => prev.map(v => v.id === vinoId ? { ...v, immagine_url: url } : v));
          clearViniCache();
        }}
      />
    </>
  );

  // Mobile Layout
  if (onBack) {
    return (
      <div className="mobile-content-wrapper min-h-screen bg-gradient-to-b from-red-950 via-slate-950 to-slate-950">
        {/* Header Mobile - con safe area per status bar */}
        <div className="sticky top-0 z-50 bg-black/30 backdrop-blur-md pb-3 px-4 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
          <button 
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Wine className="h-5 w-5" />
              Lista Vini
            </h1>
            <p className="text-red-200 text-xs">
              {viniFiltrati.length} vini
              {searchQuery || filtroTipo || filtroNazione || filtroRegione ? 
                ` (filtrati da ${vini.length})` : ''}
            </p>
          </div>
        </div>

        {/* Search Bar Mobile */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca vino..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSearchQuery(e.target.value);
              }}
              className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
          </div>
        </div>

        {/* Filtri Mobile */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white text-sm min-w-[120px]"
          >
            <option value="" className="text-gray-900">Tutti i tipi</option>
            {filtri.tipi?.map(t => (
              <option key={t} value={t} className="text-gray-900">{TIPO_VINO_LABELS[t] || t}</option>
            ))}
          </select>
          <select
            value={filtroNazione}
            onChange={(e) => { setFiltroNazione(e.target.value); setFiltroRegione(''); }}
            className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white text-sm min-w-[120px]"
          >
            <option value="" className="text-gray-900">Tutte le nazioni</option>
            {filtri.nazioni?.map(n => (
              <option key={n} value={n} className="text-gray-900">{n}</option>
            ))}
          </select>
        </div>

        {/* Pulsanti Azione Mobile */}
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {/* Aggiorna da Fatture */}
          <button
            onClick={aggiornaDaFatture}
            disabled={aggiornandoFT}
            data-testid="aggiorna-da-ft-mobile-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-green-500 text-white rounded-lg text-sm disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${aggiornandoFT ? 'animate-spin' : ''}`} />
            {aggiornandoFT ? 'Verifico prezzi…' : 'Aggiorna da FT'}
          </button>
          <button
            onClick={() => setShowVerificaCosti(true)}
            data-testid="verifica-costi-mobile-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-800 text-white rounded-lg text-sm"
          >
            <ShieldCheck className="h-4 w-4" />
            Verifica costi
          </button>
          
          {/* Importa da PDF */}
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-500 text-white rounded-lg text-sm"
            data-testid="upload-fattura-mobile-btn"
          >
            <FileUp className="h-4 w-4" />
            Importa PDF
          </button>
          
          {/* Aggiungi Vino Manuale */}
          <button
            onClick={() => setShowAggiungiVino(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm"
            data-testid="aggiungi-vino-btn"
          >
            <Plus className="h-4 w-4" />
            Aggiungi
          </button>
          
          {/* Categorizza Tutti */}
          <button
            onClick={handleCategorizzaTutti}
            disabled={categorizzando}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm disabled:opacity-50"
            data-testid="categorizza-btn"
          >
            {categorizzando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            {categorizzando ? 'Categorizzo...' : 'Categorizza'}
          </button>

          {/* Verifica carta con AI */}
          <button
            onClick={() => setShowVerificaAI(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm"
            data-testid="verifica-ai-btn"
          >
            <Sparkles className="h-4 w-4" />
            Verifica AI
          </button>

          {/* Recupera vini dalla vecchia carta PDF */}
          <button
            onClick={() => setShowVecchiaCarta(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-700 text-white rounded-lg text-sm"
            data-testid="vecchia-carta-btn"
          >
            <ArchiveRestore className="h-4 w-4" />
            Vecchia carta
          </button>

          {/* Descrizioni da sommelier (3 esempi da approvare, poi tutta la carta) */}
          <button
            onClick={() => setShowDescrizioni(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-stone-700 text-white rounded-lg text-sm"
            data-testid="descrizioni-sommelier-btn"
          >
            <PenLine className="h-4 w-4" />
            Descrizioni
          </button>

          {/* Possibili doppioni da confermare */}
          <button
            onClick={() => setShowDoppioni(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-stone-500 text-white rounded-lg text-sm"
            data-testid="doppioni-btn"
          >
            <GitMerge className="h-4 w-4" />
            Doppioni
          </button>
          
          {/* Genera descrizioni AI */}
          <button
            onClick={handleGeneraDescrizioniAI}
            disabled={generandoDescrizioni || selectedVini.size === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {generandoDescrizioni ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {generandoDescrizioni ? 'Generando...' : `Genera AI${selectedVini.size > 0 ? ` (${selectedVini.size})` : ''}`}
          </button>
          
          {/* Rigenera TUTTA la lista (riclassifica forzata + descrizioni) */}
          <button
            onClick={handleRigeneraLista}
            disabled={riclassificando || generandoDescrizioni || vini.length === 0}
            data-testid="rigenera-lista-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-lg text-sm disabled:opacity-50"
            title="Riclassifica FORZATA + rigenera tutte le descrizioni (corregge liste errate)"
          >
            {(riclassificando || generandoDescrizioni) ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Rigenera Lista
          </button>
          
          {/* QR Carta Vini digitale (mobile) */}
          <button
            onClick={() => setShowQRCarta(true)}
            data-testid="qr-carta-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-700 text-white rounded-lg text-sm"
            title="Carta dei vini digitale con QR code"
          >
            <QrCode className="h-4 w-4" />
            QR Carta
          </button>

          {/* Pubblica selezione sulla carta QR (mobile) */}
          <button
            onClick={handlePubblicaSelezione}
            data-testid="pubblica-selezione-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm"
            title="Pubblica solo i vini selezionati sulla Carta QR pubblica"
          >
            <Globe className="h-4 w-4" />
            Pubblica Selezione
          </button>

          {/* Ricarico vini (mobile) */}
          <button
            onClick={apriRicarico}
            data-testid="ricarico-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-teal-700 text-white rounded-lg text-sm"
            title="Imposta moltiplicatore di ricarico e soglia minima"
          >
            <Percent className="h-4 w-4" />
            Ricarico
          </button>
          
          {/* Riclassifica con AI */}
          <button
            onClick={handleRiclassificaAI}
            disabled={riclassificando}
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm disabled:opacity-50"
            data-testid="riclassifica-ai-btn"
          >
            {riclassificando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {riclassificando ? 'Riclassificando...' : 'Riclassifica AI'}
          </button>
          
          {/* Pulisci non-vini */}
          <button
            onClick={handlePulisciNonVini}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-700 text-white rounded-lg text-sm hover:bg-red-600"
            data-testid="pulisci-non-vini-btn"
          >
            <Trash2 className="h-4 w-4" />
            Pulisci Non-Vini
          </button>
        </div>

        {/* Lista Vini Mobile */}
        <div className="px-4 pb-20">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
          ) : Object.keys(grouped).length === 0 ? (
            <div className="text-center py-20">
              <Wine className="h-16 w-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/60">Nessun vino trovato</p>
            </div>
          ) : (
            Object.entries(grouped).map(([tipoVino, nazioniData]) => (
              <div key={tipoVino} className="mb-4">
                {/* Tipo Vino Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-xl px-4 py-2 flex items-center justify-between">
                  <span className="font-bold text-white">{TIPO_VINO_LABELS[tipoVino] || tipoVino}</span>
                  <span className="text-white/80 text-sm">
                    {Object.values(nazioniData).reduce((acc, r) => acc + Object.values(r).reduce((a, v) => a + v.length, 0), 0)} vini
                  </span>
                </div>
                
                {/* Vini Cards */}
                <div className="bg-white rounded-b-xl overflow-hidden divide-y divide-gray-100">
                  {Object.entries(nazioniData).map(([nazione, regioniData]) => (
                    Object.entries(regioniData).map(([regione, viniList]) => (
                      viniList.map(vino => (
                        <div 
                          key={vino.id}
                          className="p-4 hover:bg-gray-50 active:bg-gray-100"
                          onClick={() => toggleVino(vino.id)}
                        >
                          {/* Riga principale: checkbox + nome + prezzi */}
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                              selectedVini.has(vino.id) ? 'bg-red-600 border-red-600' : 'border-gray-300'
                            }`}>
                              {selectedVini.has(vino.id) && <Check className="h-3 w-3 text-white" />}
                            </div>
                            
                            {/* Nome e cantina - con edit inline */}
                            <div className="flex-1 min-w-0" onClick={e => e.stopPropagation()}>
                              {editingNomeVino === vino.id ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={tempNomeVino}
                                    onChange={(e) => setTempNomeVino(e.target.value)}
                                    className="flex-1 min-w-0 w-full py-1 px-2 border rounded text-sm"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveNomeVino(vino);
                                      if (e.key === 'Escape') setEditingNomeVino(null);
                                    }}
                                  />
                                  <button
                                    onClick={() => handleSaveNomeVino(vino)}
                                    className="flex-shrink-0 p-1 text-green-600"
                                  >
                                    <Save className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingNomeVino(null)}
                                    className="flex-shrink-0 p-1 text-red-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <p className="font-semibold text-gray-900 leading-tight">
                                    {vino.nome}
                                    {vino.nome_modificato_manualmente && (
                                      <span className="ml-1 text-xs text-blue-500">✎</span>
                                    )}
                                  </p>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingNomeVino(vino.id);
                                      setTempNomeVino(vino.nome || '');
                                    }}
                                    className="p-1 text-gray-400"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setVinoPerImmagine(vino); }}
                                    className={`p-1 ${vino.immagine_url ? 'text-purple-500' : 'text-gray-400'}`}
                                    title="Abbina immagine bottiglia"
                                    data-testid={`abbina-img-btn-${vino.id}`}
                                  >
                                    <ImageIcon className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEliminaVino(vino.id, vino.nome);
                                    }}
                                    className="p-1 text-red-400 hover:text-red-600"
                                    data-testid={`delete-vino-${vino.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              {vino.cantina && (
                                <p className="text-sm text-blue-600">{vino.cantina}</p>
                              )}
                            </div>
                            
                            {/* Prezzi compatti */}
                            <div className="flex-shrink-0 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <div className="text-right">
                                <p className="text-[10px] text-gray-400">Acq.</p>
                                <p className="text-xs text-gray-600">{vino.prezzo_acquisto ? `€${vino.prezzo_acquisto.toFixed(0)}` : '—'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-gray-400">Vend.</p>
                                <p className="text-sm font-bold text-red-700">€{vino.prezzo_vendita?.toFixed(0) || 0}</p>
                              </div>
                              <div className={`px-2 py-1 rounded-lg ${
                                !vino.prezzo_acquisto ? 'bg-gray-100' :
                                vino.margine_perc >= 60 ? 'bg-green-100' :
                                vino.margine_perc >= 40 ? 'bg-blue-100' :
                                'bg-amber-100'
                              }`}>
                                <p className={`text-sm font-bold ${
                                  !vino.prezzo_acquisto ? 'text-gray-400' :
                                  vino.margine_perc >= 60 ? 'text-green-700' :
                                  vino.margine_perc >= 40 ? 'text-blue-700' :
                                  'text-amber-700'
                                }`}>
                                  {vino.prezzo_acquisto ? `${vino.margine_perc?.toFixed(0) || 0}%` : '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Dettagli sotto */}
                          <div className="mt-2 ml-9">
                            {/* Tags */}
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{nazione}</span>
                              {regione && regione !== nazione && (
                                <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{regione}</span>
                              )}
                              {vino.vitigno && (
                                <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">{vino.vitigno}</span>
                              )}
                              {vino.gradazione && (
                                <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{vino.gradazione}</span>
                              )}
                            </div>
                            
                            {/* Descrizione */}
                            {vino.descrizione && (
                              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{vino.descrizione}</p>
                            )}
                            
                            {/* Abbinamenti */}
                            {vino.abbinamenti && (
                              <p className="text-sm text-green-700 mt-1">🍽️ <span className="italic">{vino.abbinamenti}</span></p>
                            )}
                            
                            {/* Riconoscimenti */}
                            {vino.riconoscimenti && (
                              <p className="text-sm text-amber-700 mt-1" data-testid="vino-riconoscimenti">🏆 <span className="italic">{vino.riconoscimenti}</span></p>
                            )}
                            
                            {/* Pulsante genera AI singolo (mobile) */}
                            {!vino.descrizione && (
                              <button
                                onClick={(e) => handleGeneraDescrizioneSingolo(vino, e)}
                                disabled={generandoSingoloId === vino.id || generandoDescrizioni}
                                className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm disabled:opacity-50"
                              >
                                {generandoSingoloId === vino.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Wand2 className="h-4 w-4" />
                                )}
                                {generandoSingoloId === vino.id ? 'Generando...' : 'Genera Descrizione AI'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    ))
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* FAB per azioni */}
        {selectedVini.size > 0 && (
          <div className="fixed bottom-6 right-4 left-4 flex gap-2">
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-white text-red-700 rounded-xl shadow-lg font-semibold"
            >
              <Printer className="h-5 w-5" />
              Stampa ({selectedVini.size})
            </button>
          </div>
        )}

        {/* Modali (condivisi col desktop) */}
        {renderModali()}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-900 to-red-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Wine className="h-7 w-7" />
                Lista Vini
              </h1>
              <p className="text-red-200 text-sm mt-1">
                Gestisci i prezzi e crea la carta vini stampabile
              </p>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-red-200 text-sm">
                {selectedVini.size}/{viniFiltrati.length} selezionati
                {(searchQuery || filtroTipo || filtroNazione || filtroRegione) && (
                  <span className="ml-1 opacity-75">
                    (filtrati da {vini.length})
                  </span>
                )}
              </span>
              
              <button
                onClick={refreshVini}
                className="flex items-center gap-2 px-3 py-2 bg-red-800 hover:bg-red-700 rounded-lg transition-colors"
                title="Aggiorna lista vini"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => setShowSettings(true)}
                data-testid="stampa-impostazioni-btn"
                className="flex items-center gap-2 px-3 py-2 bg-red-800 hover:bg-red-700 rounded-lg transition-colors"
              >
                <Settings className="h-4 w-4" />
                Impostazioni
              </button>
              
              <button
                onClick={handlePrint}
                disabled={selectedVini.size === 0}
                data-testid="stampa-lista-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white text-red-900 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Printer className="h-5 w-5" />
                Stampa Lista
              </button>
              
              <button
                onClick={() => {
                  const url = `${API}/api/magazzino/vini/lista-prezzi-pdf`;
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'lista_prezzi_vini.pdf');
                  // Aggiungi header tenant
                  fetch(url, {
                    headers: getAuthHeaders()
                  })
                  .then(res => res.blob())
                  .then(blob => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    link.href = blobUrl;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(blobUrl);
                    toast.success('PDF Lista Prezzi scaricato');
                  })
                  .catch(err => {
                    console.error('Errore download PDF:', err);
                    toast.error('Errore nel download del PDF');
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                title="Scarica PDF con nome vino, codice a barre e prezzo"
              >
                <Download className="h-5 w-5" />
                PDF Prezzi
              </button>
              
              <button
                onClick={() => {
                  const url = `${API}/api/magazzino/vini/export-excel`;
                  fetch(url, { headers: getAuthHeaders() })
                  .then(res => res.blob())
                  .then(blob => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.setAttribute('download', 'lista_vini.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(blobUrl);
                    toast.success('Excel scaricato');
                  })
                  .catch(() => toast.error('Errore download Excel'));
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                data-testid="export-excel-btn"
              >
                <Sheet className="h-5 w-5" />
                Excel
              </button>
              
              <button
                onClick={() => {
                  const url = `${API}/api/magazzino/vini/export-word`;
                  fetch(url, { headers: getAuthHeaders() })
                  .then(res => res.blob())
                  .then(blob => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.setAttribute('download', 'carta_dei_vini.docx');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(blobUrl);
                    toast.success('Word scaricato');
                  })
                  .catch(() => toast.error('Errore download Word'));
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-600 transition-colors"
                data-testid="export-word-btn"
              >
                <FileType className="h-5 w-5" />
                Word
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar con filtri e azioni */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          {/* Ricerca e filtri */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca vino..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Filtro Tipo */}
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="py-2 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
            >
              <option value="">Tutti i tipi</option>
              {filtri.tipi.map(t => (
                <option key={t} value={t}>{TIPO_VINO_LABELS[t] || t}</option>
              ))}
            </select>

            {/* Filtro Nazione */}
            <select
              value={filtroNazione}
              onChange={(e) => { setFiltroNazione(e.target.value); setFiltroRegione(''); }}
              className="py-2 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
            >
              <option value="">Tutte le nazioni</option>
              {filtri.nazioni.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            {/* Filtro Regione */}
            <select
              value={filtroRegione}
              onChange={(e) => setFiltroRegione(e.target.value)}
              className="py-2 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
            >
              <option value="">Tutte le regioni</option>
              {filtri.regioni.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Azioni rapide */}
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
            <button
              onClick={handleSelectAll}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                selectAll 
                  ? 'bg-red-100 border-red-300 text-red-700' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {selectAll ? <Check className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {selectAll ? 'Deseleziona' : 'Seleziona tutti'}
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Marginalità:</span>
              <input
                type="number"
                value={marginalitaDefault}
                onChange={(e) => setMarginalitaDefault(parseInt(e.target.value) || 70)}
                min="0"
                max="90"
                className="w-16 py-1 px-2 border border-gray-200 rounded text-sm text-center"
              />
              <span className="text-sm text-gray-600">%</span>
              <button
                onClick={applicaMarginalitaATutti}
                disabled={selectedVini.size === 0}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <Percent className="h-3.5 w-3.5" />
                Applica
              </button>
            </div>

            <button
              onClick={handleGeneraDescrizioniAI}
              disabled={selectedVini.size === 0 || generandoDescrizioni}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
            >
              {generandoDescrizioni ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {generandoDescrizioni 
                ? `${progressGen.current}/${progressGen.total}` 
                : 'Genera descrizioni AI'}
            </button>

            <button
              onClick={aggiornaDaFatture}
              disabled={aggiornandoFT}
              data-testid="aggiorna-da-ft-btn"
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-60"
              title="Rilegge le fatture e aggiorna i prezzi in carta se ci sono aumenti"
            >
              <RefreshCw className={`h-4 w-4 ${aggiornandoFT ? 'animate-spin' : ''}`} />
              {aggiornandoFT ? 'Verifico prezzi…' : 'Aggiorna da Fatture'}
            </button>

            <button
              onClick={() => setShowVerificaCosti(true)}
              data-testid="verifica-costi-btn"
              className="flex items-center gap-2 px-3 py-2 bg-emerald-800 text-white rounded-lg text-sm hover:bg-emerald-900"
              title="Confronta il costo di ogni vino con la sua ultima fattura e corregge gli errori"
            >
              <ShieldCheck className="h-4 w-4" />
              Verifica costi
            </button>

            {/* Riclassifica con AI (desktop) */}
            <button
              onClick={handleRiclassificaAI}
              disabled={riclassificando || generandoDescrizioni}
              className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50"
              title="Riclassifica cantina, nazione, regione e tipo con AI"
              data-testid="riclassifica-ai-desktop-btn"
            >
              {riclassificando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {riclassificando ? 'Riclassificando...' : 'Riclassifica AI'}
            </button>

            {/* Rigenera TUTTA la lista (desktop) */}
            <button
              onClick={handleRigeneraLista}
              disabled={riclassificando || generandoDescrizioni || vini.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              title="Riclassifica FORZATA + rigenera tutte le descrizioni (corregge liste errate)"
              data-testid="rigenera-lista-desktop-btn"
            >
              {(riclassificando || generandoDescrizioni) ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Rigenera Lista
            </button>

            {/* QR Carta Vini digitale (desktop) */}
            <button
              onClick={() => setShowQRCarta(true)}
              data-testid="qr-carta-desktop-btn"
              className="flex items-center gap-2 px-3 py-2 bg-purple-700 text-white rounded-lg text-sm hover:bg-purple-800"
              title="Carta dei vini digitale con QR code"
            >
              <QrCode className="h-4 w-4" />
              QR Carta
            </button>

            {/* Pubblica selezione sulla carta QR (desktop) */}
            <button
              onClick={handlePubblicaSelezione}
              data-testid="pubblica-selezione-desktop-btn"
              className="flex items-center gap-2 px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm hover:bg-emerald-800"
              title="Pubblica solo i vini selezionati sulla Carta QR pubblica"
            >
              <Globe className="h-4 w-4" />
              Pubblica Selezione
            </button>

            {/* Ricarico vini (desktop) */}
            <button
              onClick={apriRicarico}
              data-testid="ricarico-desktop-btn"
              className="flex items-center gap-2 px-3 py-2 bg-teal-700 text-white rounded-lg text-sm hover:bg-teal-800"
              title="Imposta moltiplicatore di ricarico e soglia minima"
            >
              <Percent className="h-4 w-4" />
              Ricarico
            </button>

            {/* Upload Fattura PDF */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
              title="Importa vini da fattura PDF"
              data-testid="upload-fattura-btn"
            >
              <FileUp className="h-4 w-4" />
              Importa da PDF
            </button>

            {/* Aggiungi Vino Manuale */}
            <button
              onClick={() => setShowAggiungiVino(true)}
              className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600"
              title="Aggiungi vino manualmente"
              data-testid="aggiungi-vino-desktop-btn"
            >
              <Plus className="h-4 w-4" />
              Aggiungi Vino
            </button>

            {/* Categorizza Tutti */}
            <button
              onClick={handleCategorizzaTutti}
              disabled={categorizzando}
              className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50"
              title="Ri-categorizza tutti i vini"
              data-testid="categorizza-desktop-btn"
            >
              {categorizzando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {categorizzando ? 'Categorizzo...' : 'Categorizza'}
            </button>

            {/* Verifica carta con AI */}
            <button
              onClick={() => setShowVerificaAI(true)}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
              title="Controlla produttori, uvaggi e categorie sulle schede ufficiali con l'AI"
              data-testid="verifica-ai-desktop-btn"
            >
              <Sparkles className="h-4 w-4" />
              Verifica AI
            </button>

            {/* Recupera vini dalla vecchia carta PDF */}
            <button
              onClick={() => setShowVecchiaCarta(true)}
              className="flex items-center gap-2 px-3 py-2 bg-amber-700 text-white rounded-lg text-sm hover:bg-amber-800"
              title="Vini della vecchia carta in PDF che non risultano in fattura: scegli quali recuperare"
              data-testid="vecchia-carta-desktop-btn"
            >
              <ArchiveRestore className="h-4 w-4" />
              Vecchia carta
            </button>

            {/* Descrizioni da sommelier (3 esempi da approvare, poi tutta la carta) */}
            <button
              onClick={() => setShowDescrizioni(true)}
              className="flex items-center gap-2 px-3 py-2 bg-stone-700 text-white rounded-lg text-sm hover:bg-stone-800"
              title="Riscrive tutte le descrizioni in stile sommelier, solo con dati verificati"
              data-testid="descrizioni-sommelier-desktop-btn"
            >
              <PenLine className="h-4 w-4" />
              Descrizioni
            </button>

            {/* Possibili doppioni da confermare */}
            <button
              onClick={() => setShowDoppioni(true)}
              className="flex items-center gap-2 px-3 py-2 bg-stone-500 text-white rounded-lg text-sm hover:bg-stone-600"
              title="Vini forse doppi: scegli se unirli o tenerli separati"
              data-testid="doppioni-desktop-btn"
            >
              <GitMerge className="h-4 w-4" />
              Doppioni
            </button>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-300 mx-1" />

            {/* Selezioni */}
            <button
              onClick={() => setShowSalvaSelezione(true)}
              disabled={selectedVini.size === 0}
              className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
              title="Salva la selezione corrente"
              data-testid="salva-selezione-btn"
            >
              <Bookmark className="h-4 w-4" />
              Salva Selezione
            </button>

            <div className="relative">
              <button
                onClick={() => setShowSelezioniDropdown(!showSelezioniDropdown)}
                disabled={selezioni.length === 0}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                title="Carica una selezione salvata"
                data-testid="carica-selezione-btn"
              >
                <FolderOpen className="h-4 w-4" />
                Carica Selezione
                <ChevronDown className={`h-4 w-4 transition-transform ${showSelezioniDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown selezioni */}
              {showSelezioniDropdown && (
                <div data-dropdown-selezioni className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  {loadingSelezioni ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : selezioni.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      Nessuna selezione salvata
                    </div>
                  ) : (
                    <div className="py-1">
                      {selezioni.map(sel => (
                        <div
                          key={sel.id}
                          onClick={() => handleCaricaSelezione(sel)}
                          className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 cursor-pointer group"
                          data-testid={`selezione-item-${sel.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{sel.nome}</p>
                            <p className="text-xs text-gray-500">
                              {sel.num_vini} vini
                              {sel.descrizione && ` • ${sel.descrizione}`}
                            </p>
                          </div>
                          <button
                            onClick={(e) => handleEliminaSelezione(sel, e)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Elimina selezione"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lista Vini */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-red-600" />
          </div>
        ) : viniFiltrati.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Wine className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">
              {vini.length === 0 ? 'Nessun vino trovato' : 'Nessun vino con questi filtri'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {vini.length === 0 
                ? 'Carica prima un menu vini o aggiungi vini dalla sezione F&B Cost'
                : 'Prova a modificare i filtri di ricerca'}
            </p>
            {vini.length === 0 && (
              <button 
                onClick={refreshVini}
                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="h-4 w-4" />
                Ricarica lista vini
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {TIPO_VINO_ORDER.map(tipoKey => {
              const tipoData = grouped[tipoKey];
              if (!tipoData || Object.keys(tipoData).length === 0) return null;
              
              return (
                <div key={tipoKey} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Header Tipo */}
                  <div className="bg-gradient-to-r from-red-800 to-red-700 text-white px-4 py-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Wine className="h-5 w-5" />
                      {TIPO_VINO_LABELS[tipoKey] || tipoKey}
                      <span className="ml-auto text-sm font-normal opacity-75">
                        {Object.values(tipoData).flatMap(n => Object.values(n).flat()).length} vini
                      </span>
                    </h2>
                  </div>
                  
                  {/* Contenuto per nazione/regione */}
                  <div className="divide-y divide-gray-100">
                    {Object.entries(tipoData).sort().map(([nazione, regioniData]) => (
                      <div key={nazione}>
                        {/* Header Nazione */}
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Globe className="h-4 w-4 text-gray-400" />
                            {nazione}
                          </h3>
                        </div>
                        
                        {/* Regioni */}
                        {Object.entries(regioniData).sort().map(([regione, viniRegione]) => (
                          <div key={regione} className="pl-6">
                            {/* Header Regione */}
                            <div className="px-4 py-1.5 bg-gray-50/50 border-b border-gray-50">
                              <h4 className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                                <MapPin className="h-3 w-3" />
                                {regione}
                              </h4>
                            </div>
                            
                            {/* Vini della regione */}
                            {viniRegione.map(vino => (
                              <div
                                key={vino.id}
                                onClick={() => toggleVino(vino.id)}
                                className={`grid grid-cols-12 gap-1 sm:gap-3 px-2 sm:px-4 py-3 cursor-pointer transition-colors items-start border-b border-gray-50 ${
                                  selectedVini.has(vino.id) 
                                    ? 'bg-red-50 hover:bg-red-100' 
                                    : 'hover:bg-gray-50'
                                }`}
                              >
                                {/* Checkbox */}
                                <div className="col-span-1 flex justify-center pt-1">
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                    selectedVini.has(vino.id) 
                                      ? 'bg-red-600 border-red-600' 
                                      : 'border-gray-300'
                                  }`}>
                                    {selectedVini.has(vino.id) && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                </div>
                                
                                {/* Nome e descrizione */}
                                <div className="col-span-6 sm:col-span-5">
                                  {editingNomeVino === vino.id ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="text"
                                        value={tempNomeVino}
                                        onChange={(e) => setTempNomeVino(e.target.value)}
                                        className="flex-1 py-1 px-2 border rounded text-sm"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveNomeVino(vino);
                                          if (e.key === 'Escape') setEditingNomeVino(null);
                                        }}
                                      />
                                      <button
                                        onClick={() => handleSaveNomeVino(vino)}
                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                      >
                                        <Save className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => setEditingNomeVino(null)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 group">
                                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                                        {vino.nome}
                                        {vino.nome_modificato_manualmente && (
                                          <span className="ml-1 text-xs text-blue-500" title="Nome modificato manualmente">✎</span>
                                        )}
                                      </p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingNomeVino(vino.id);
                                          setTempNomeVino(vino.nome || '');
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Modifica nome"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setVinoPerImmagine(vino); }}
                                        className={`p-1 rounded ${vino.immagine_url ? 'text-purple-500' : 'text-gray-400 opacity-0 group-hover:opacity-100'} hover:text-purple-600 hover:bg-purple-50 transition-opacity`}
                                        title="Abbina immagine bottiglia"
                                        data-testid={`abbina-img-desktop-btn-${vino.id}`}
                                      >
                                        <ImageIcon className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                  {vino.cantina && (
                                    <p className="text-xs text-blue-600 mt-0.5">
                                      {vino.cantina}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {vino.vitigno && (
                                      <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                        {vino.vitigno}
                                      </span>
                                    )}
                                    {vino.gradazione && (
                                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                        {vino.gradazione}
                                      </span>
                                    )}
                                    {vino.annata && (
                                      <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                        {vino.annata}
                                      </span>
                                    )}
                                    {vino.num_bottiglie_cartone > 1 && (
                                      <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                        CT {vino.num_bottiglie_cartone}
                                      </span>
                                    )}
                                  </div>
                                  {vino.descrizione && (
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                      {vino.descrizione}
                                    </p>
                                  )}
                                  {vino.abbinamenti && (
                                    <p className="text-xs text-green-600 mt-1 line-clamp-1">
                                      🍽️ {vino.abbinamenti}
                                    </p>
                                  )}
                                  {vino.riconoscimenti && (
                                    <p className="text-xs text-amber-700 mt-1 line-clamp-1" data-testid="vino-riconoscimenti-desktop">
                                      🏆 {vino.riconoscimenti}
                                    </p>
                                  )}
                                  {/* Pulsante genera AI singolo */}
                                  {!vino.descrizione && (
                                    <button
                                      onClick={(e) => handleGeneraDescrizioneSingolo(vino, e)}
                                      disabled={generandoSingoloId === vino.id || generandoDescrizioni}
                                      className="mt-2 flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 disabled:opacity-50"
                                      data-testid={`genera-ai-${vino.id}`}
                                    >
                                      {generandoSingoloId === vino.id ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <Wand2 className="h-3 w-3" />
                                      )}
                                      {generandoSingoloId === vino.id ? 'Generando...' : 'Genera AI'}
                                    </button>
                                  )}
                                </div>
                                
                                {/* Prezzo acquisto */}
                                <div className="col-span-2 sm:col-span-2 text-center" onClick={e => e.stopPropagation()}>
                                  <p className="text-[10px] sm:text-xs text-gray-500">
                                    Acq.
                                  </p>
                                  <p className="text-xs sm:text-sm font-medium text-gray-700">
                                    {vino.prezzo_acquisto ? `€${vino.prezzo_acquisto.toFixed(0)}` : '—'}
                                  </p>
                                </div>
                                
                                {/* Prezzo vendita */}
                                <div className="col-span-2 sm:col-span-2 text-center" onClick={e => e.stopPropagation()}>
                                  <p className="text-[10px] sm:text-xs text-gray-500">Vend.</p>
                                  {editingVino === vino.id ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        value={tempPrezzoVendita}
                                        onChange={(e) => setTempPrezzoVendita(e.target.value)}
                                        className="w-12 sm:w-16 py-1 px-1 sm:px-2 border rounded text-xs sm:text-sm text-center"
                                        step="0.5"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSavePrezzoVendita(vino);
                                          if (e.key === 'Escape') setEditingVino(null);
                                        }}
                                      />
                                      <button
                                        onClick={() => handleSavePrezzoVendita(vino)}
                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                      >
                                        <Save className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center gap-1">
                                      <p className="text-xs sm:text-sm font-bold text-red-700">
                                        €{vino.prezzo_vendita?.toFixed(0) || '0'}
                                      </p>
                                      <button
                                        onClick={() => {
                                          setEditingVino(vino.id);
                                          setTempPrezzoVendita(vino.prezzo_vendita?.toFixed(2) || '0');
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Margine */}
                                <div className="col-span-1 sm:col-span-2 text-center" onClick={e => e.stopPropagation()}>
                                  <p className={`text-xs sm:text-sm font-bold ${
                                    !vino.prezzo_acquisto ? 'text-gray-400' :
                                    vino.margine_perc >= 60 ? 'text-green-600' :
                                    vino.margine_perc >= 40 ? 'text-blue-600' :
                                    vino.margine_perc >= 20 ? 'text-amber-600' :
                                    'text-red-600'
                                  }`}>
                                    {vino.prezzo_acquisto ? `${vino.margine_perc?.toFixed(0) || 0}%` : '—'}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {vino.prezzo_acquisto ? `€${((vino.prezzo_vendita || 0) - vino.prezzo_acquisto).toFixed(2)}` : ''}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sezione Liquori/Distillati */}
      {liquori.length > 0 && (
        <div className="mt-8 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-amber-900 text-white p-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span>🥃</span> Liquori e Distillati
              <span className="text-sm font-normal bg-amber-800 px-2 py-0.5 rounded-full ml-2">
                {liquori.length}
              </span>
              <span className="text-sm font-normal ml-auto">
                In stampa: {liquori.filter(l => l.includi_stampa !== false).length}
              </span>
            </h2>
          </div>
          
          <div className="p-4">
            {/* Header tabella */}
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide pb-2 border-b mb-2">
              <div className="col-span-1 text-center">Stampa</div>
              <div className="col-span-4">Nome</div>
              <div className="col-span-2 text-right">Costo Bicch.</div>
              <div className="col-span-2 text-right">€ Vendita</div>
              <div className="col-span-1 text-right">Ricarico</div>
              <div className="col-span-2">Categoria</div>
            </div>
            
            {/* Raggruppa per categoria */}
            {Object.entries(
              liquori.reduce((acc, l) => {
                if (!acc[l.categoria]) acc[l.categoria] = [];
                acc[l.categoria].push(l);
                return acc;
              }, {})
            ).map(([categoria, items]) => (
              <div key={categoria} className="mb-4 last:mb-0">
                <h3 className="text-sm font-semibold text-amber-800 bg-amber-100 px-3 py-1.5 rounded-lg mb-2">
                  {categoria} ({items.length})
                </h3>
                <div className="space-y-1">
                  {items.map((liquore) => (
                    <div 
                      key={liquore.id} 
                      className={`grid grid-cols-12 gap-2 items-center py-2 px-3 rounded-lg transition-colors ${
                        liquore.includi_stampa === false 
                          ? 'bg-gray-100 opacity-60' 
                          : 'bg-amber-50 hover:bg-amber-100'
                      }`}
                    >
                      {/* Checkbox stampa */}
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={liquore.includi_stampa !== false}
                          onChange={(e) => updateLiquore(liquore.id, 'includi_stampa', e.target.checked)}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                      </div>
                      
                      {/* Nome */}
                      <div className="col-span-4">
                        <p className="font-medium text-gray-800 text-sm">{liquore.nome}</p>
                      </div>
                      
                      {/* Costo bicchiere */}
                      <div className="col-span-2 text-right">
                        <p className="text-sm text-gray-500">€{liquore.costo_bicchiere?.toFixed(2)}</p>
                      </div>
                      
                      {/* Prezzo vendita (bicchiere) */}
                      <div className="col-span-2">
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={liquore.prezzo_menu || ''}
                          onChange={(e) => updateLiquore(liquore.id, 'prezzo_menu', e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1 text-sm text-right border rounded focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                      
                      {/* Percentuale ricarico */}
                      <div className="col-span-1 text-right">
                        <span className={`text-sm font-medium ${
                          liquore.percentuale_ricarico >= 300 ? 'text-green-600' :
                          liquore.percentuale_ricarico >= 200 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {liquore.percentuale_ricarico}%
                        </span>
                      </div>
                      
                      {/* Categoria badge */}
                      <div className="col-span-2">
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                          {liquore.categoria}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Salva Selezione */}
      {showSalvaSelezione && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="bg-amber-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Bookmark className="h-5 w-5" />
                Salva Selezione
              </h2>
              <button onClick={() => setShowSalvaSelezione(false)} className="p-1 hover:bg-amber-600 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                Stai salvando una selezione di <strong>{selectedVini.size}</strong> vini.
                Potrai ricaricarla in seguito per stampare rapidamente.
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome selezione *
                </label>
                <input
                  type="text"
                  value={nomeSelezione}
                  onChange={(e) => setNomeSelezione(e.target.value)}
                  placeholder="Es. Vini al bicchiere, Serata pesce..."
                  className="w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  autoFocus
                  data-testid="nome-selezione-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione (opzionale)
                </label>
                <textarea
                  value={descrizioneSelezione}
                  onChange={(e) => setDescrizioneSelezione(e.target.value)}
                  placeholder="Note aggiuntive..."
                  rows={2}
                  className="w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="descrizione-selezione-input"
                />
              </div>
              
              {/* Suggerimenti nomi */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Suggerimenti:</p>
                <div className="flex flex-wrap gap-2">
                  {['Vini al bicchiere', 'Menu degustazione', 'Serata pesce', 'Serata carne', 'Top 10'].map(sug => (
                    <button
                      key={sug}
                      onClick={() => setNomeSelezione(sug)}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => setShowSalvaSelezione(false)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Annulla
              </button>
              <button
                onClick={handleSalvaSelezione}
                disabled={!nomeSelezione.trim()}
                className="flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="conferma-salva-selezione-btn"
              >
                <Save className="h-4 w-4" />
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Impostazioni Stampa */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="bg-red-900 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-lg font-semibold">Impostazioni Stampa</h2>
              <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-red-800 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Ristorante
                </label>
                <input
                  type="text"
                  value={stampaSettings.nomeRistorante}
                  onChange={(e) => setStampaSettings(s => ({ ...s, nomeRistorante: e.target.value }))}
                  placeholder="Es. Osteria del Gusto"
                  className="w-full py-2 px-3 border border-gray-200 rounded-lg"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Mostra descrizioni vini</span>
                <button
                  onClick={() => setStampaSettings(s => ({ ...s, mostraDescrizione: !s.mostraDescrizione }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stampaSettings.mostraDescrizione ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    stampaSettings.mostraDescrizione ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Mostra prezzi</span>
                <button
                  onClick={() => setStampaSettings(s => ({ ...s, mostraPrezzi: !s.mostraPrezzi }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stampaSettings.mostraPrezzi ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    stampaSettings.mostraPrezzi ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Mostra formato (0,75L)</span>
                <button
                  onClick={() => setStampaSettings(s => ({ ...s, mostraFormato: !s.mostraFormato }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stampaSettings.mostraFormato ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    stampaSettings.mostraFormato ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Pagina copertina</span>
                <button
                  onClick={() => setStampaSettings(s => ({ ...s, mostraCopertina: !s.mostraCopertina }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stampaSettings.mostraCopertina ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    stampaSettings.mostraCopertina ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {/* Toggle liquori */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Includi Liquori/Distillati</span>
                <button
                  onClick={() => setStampaSettings(s => ({ ...s, mostraLiquori: !s.mostraLiquori, soloLiquori: false }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stampaSettings.mostraLiquori ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    stampaSettings.mostraLiquori ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {/* Toggle SOLO liquori */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Stampa SOLO Liquori/Distillati</span>
                <button
                  onClick={() => setStampaSettings(s => ({ ...s, soloLiquori: !s.soloLiquori, mostraLiquori: true }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stampaSettings.soloLiquori ? 'bg-amber-600' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    stampaSettings.soloLiquori ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {/* Margini stampa */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Margini Stampa (mm)</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Superiore</label>
                    <input
                      type="number"
                      value={stampaSettings.margineSup}
                      onChange={(e) => setStampaSettings(s => ({ ...s, margineSup: parseInt(e.target.value) || 15 }))}
                      className="w-full px-2 py-1 border rounded text-sm"
                      min="5"
                      max="50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Inferiore</label>
                    <input
                      type="number"
                      value={stampaSettings.margineInf}
                      onChange={(e) => setStampaSettings(s => ({ ...s, margineInf: parseInt(e.target.value) || 15 }))}
                      className="w-full px-2 py-1 border rounded text-sm"
                      min="5"
                      max="50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Laterali</label>
                    <input
                      type="number"
                      value={stampaSettings.margineLat}
                      onChange={(e) => setStampaSettings(s => ({ ...s, margineLat: parseInt(e.target.value) || 10 }))}
                      className="w-full px-2 py-1 border rounded text-sm"
                      min="5"
                      max="50"
                    />
                  </div>
                </div>
              </div>
              
              {/* Numero pagine */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Numero Pagine (Carta dei Vini)</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={stampaSettings.numeroPagine}
                    onChange={(e) => setStampaSettings(s => ({ ...s, numeroPagine: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    min="0"
                    max="20"
                    placeholder="0 = automatico"
                    data-testid="stampa-numero-pagine"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2" data-testid="stampa-pagine-hint">
                  0 = automatico. Il numero si riferisce alle sole pagine dei vini (copertina e liquori si aggiungono).
                  I vini vengono misurati e impaginati uno a uno: il font si adatta per stare esattamente nelle pagine indicate e l'anteprima mostra il conteggio reale prima di stampare.
                </p>
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                  <div className="text-gray-500 mt-1">
                    {viniDaStampare.length} vini selezionati
                    {stampaSettings.mostraDescrizione ? ' (con descrizioni complete)' : ' (solo nomi)'}
                  </div>
                  {stampaSettings.mostraLiquori && liquori.filter(l => l.includi_stampa !== false).length > 0 && (
                    <div className="text-amber-600 mt-1">
                      + pagine liquori per {liquori.filter(l => l.includi_stampa !== false).length} liquori (conteggio nell'anteprima)
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowSettings(false)}
                className="w-full py-2 bg-red-700 text-white rounded-lg hover:bg-red-800"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Anteprima Stampa */}
      {showPrintPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-red-900 text-white p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Anteprima - {viniDaStampare.length} vini selezionati
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={executePrint}
                  data-testid="stampa-conferma-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-white text-red-900 rounded-lg hover:bg-red-50"
                >
                  <Printer className="h-4 w-4" />
                  Stampa
                </button>
                <button onClick={() => setShowPrintPreview(false)} className="p-2 hover:bg-red-800 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] bg-stone-100">
              <div ref={printRef} className="bg-white p-8 shadow-lg max-w-3xl mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
                <div className="text-center border-b-2 border-red-800 pb-4 mb-6">
                  <h1 className="text-2xl text-red-800 tracking-widest">CARTA DEI VINI</h1>
                  {stampaSettings.nomeRistorante && (
                    <p className="text-sm text-red-700 mt-1">{stampaSettings.nomeRistorante}</p>
                  )}
                </div>
                
                {/* Vini raggruppati per tipo */}
                {(() => {
                  // Raggruppa per tipo
                  const perTipo = {};
                  viniDaStampare.forEach(v => {
                    const tipo = (v.tipo_vino || 'altro').toLowerCase();
                    if (!perTipo[tipo]) perTipo[tipo] = {};
                    const regione = v.regione || v.nazione || 'Altro';
                    if (!perTipo[tipo][regione]) perTipo[tipo][regione] = [];
                    perTipo[tipo][regione].push(v);
                  });
                  
                  const ordine = ['champagne', 'bollicine', 'prosecco', 'bianco', 'rosato', 'rosso', 'altro'];
                  const labels = {
                    champagne: 'CHAMPAGNE', bollicine: 'BOLLICINE', prosecco: 'PROSECCO',
                    bianco: 'VINI BIANCHI', rosato: 'VINI ROSATI', rosso: 'VINI ROSSI', altro: 'ALTRI VINI'
                  };
                  
                  return ordine.map(tipo => {
                    const regioni = perTipo[tipo];
                    if (!regioni || Object.keys(regioni).length === 0) return null;
                    
                    return (
                      <div key={tipo} className="mb-6">
                        <h2 className="text-sm font-bold text-red-800 uppercase tracking-wider border-b border-red-200 pb-1 mb-3">
                          {labels[tipo] || tipo}
                        </h2>
                        {Object.entries(regioni).sort().map(([regione, viniReg]) => (
                          <div key={regione} className="mb-3">
                            <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">{regione}</h3>
                            {viniReg.sort((a, b) => (a.nome || '').localeCompare(b.nome || '')).map((vino, idx) => (
                              <div key={idx} className="py-2 border-b border-dotted border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 pr-4">
                                    <p className="font-bold text-gray-900 text-sm uppercase">{vino.nome}</p>
                                    {vino.cantina && (
                                      <p className="text-xs text-blue-700 font-medium">{vino.cantina}</p>
                                    )}
                                  </div>
                                  {stampaSettings.mostraPrezzi && (
                                    <p className="font-bold text-red-800 whitespace-nowrap text-sm">
                                      € {vino.prezzo_vendita?.toFixed(2)}
                                    </p>
                                  )}
                                </div>
                                {/* Info sotto: uvaggio, gradazione, descrizione, abbinamenti */}
                                {stampaSettings.mostraDescrizione && (
                                  <div className="mt-1">
                                    <p className="text-xs text-gray-600 italic">
                                      {[
                                        vino.vitigno,
                                        vino.gradazione,
                                        vino.annata
                                      ].filter(Boolean).join(' • ')}
                                    </p>
                                    {vino.descrizione && (
                                      <p className="text-xs text-gray-500 mt-0.5">{vino.descrizione}</p>
                                    )}
                                    {vino.abbinamenti && (
                                      <p className="text-xs text-green-700 mt-0.5">{vino.abbinamenti}</p>
                                    )}
                                    {vino.riconoscimenti && (
                                      <p className="text-xs text-amber-700 mt-0.5">🏆 {vino.riconoscimenti}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  });
                })()}
                
                <div className="text-center mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-400 italic">Prezzi IVA inclusa</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderModali()}
    </div>
  );
}
